import { QueryFunctionContext, useQuery, useQueryClient } from "@tanstack/react-query";
import { configuration } from "../../../configuration";
import { FetchError, GetMeetingsForTeamDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";
import { useEffect } from "react";
import { SignalRGroup } from "../../../components";
import { useSignalR } from "../../../hooks";

const useGetMeetingsForTeamRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function getMeetingsForTeamRequest({
    queryKey,
  }: QueryFunctionContext<ReturnType<typeof queryKeys.meetings.getMeetingsForTeam>>): Promise<GetMeetingsForTeamDto> {
    const accessToken = await getAccessToken();
    const [{ teamGuid }] = queryKey;

    const response = await fetch(`${configuration.apiRootUrl}/teams/${teamGuid}/meetings`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new FetchError(response);
    }

    return response.json();
  }

  return { getMeetingsForTeamRequest };
};

const useGetMeetingsForTeam = (teamGuid?: string) => {
  const { getMeetingsForTeamRequest } = useGetMeetingsForTeamRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  useEffect(() => {
    if (teamGuid !== undefined) {
      signalR.joinGroup(SignalRGroup.TeamMeetings(teamGuid));
      return () => signalR.leaveGroup(SignalRGroup.TeamMeetings(teamGuid));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamGuid]);

  useEffect(() => {
    const handleShouldRefetchTeamMeetingsList = (teamGuid: string) => {
      queryClient.invalidateQueries(queryKeys.meetings.getMeetingsForTeam(teamGuid));
    };

    signalR.onShouldRefetchTeamMeetingsList(handleShouldRefetchTeamMeetingsList);
    return () => signalR.offShouldRefetchTeamMeetingsList(handleShouldRefetchTeamMeetingsList);
  }, [signalR, queryClient]);

  return useQuery({
    queryKey: queryKeys.meetings.getMeetingsForTeam(teamGuid!),
    queryFn: getMeetingsForTeamRequest,
    enabled: teamGuid !== undefined,
  });
};

export { useGetMeetingsForTeam };
